/* eslint-disable @typescript-eslint/no-unused-vars */
import { Env } from 'config/Env'
import { Envelope } from 'utils/types/Envelope'

import { HttpClient, ApiRequestTypes } from '../../../utils/apiService/httpService'
import {
  BroadcastMessageObject,
  BroadcastMessageResponse,
  CardResponse,
  CreateBulkDiscussionsRequestData,
  CreateBulkDiscussionsResponse,
  CreateThreadData,
  PatiendDiscussionsStatusResponse,
  ThreadCreateResponse,
} from '../../referrals/types'
import {
  DiscussionHeaderDataType,
  DiscussionMemberListReqBody,
  DiscussionMessagePostedRequest,
  DiscussionMemberListResBody,
  IsParticipantReqBody,
  PinDiscussionReqBody,
  PinDiscussionResponse,
  MentionedDiscussionsRequest,
  MentionedDiscussionsResponse,
  MetaDataType,
  PinnedDiscussionsResponse,
  RevalidationReqBody,
  UnpinDiscussionResponse,
  UserToDiscussionReqBody,
} from '../types'

const REFERRAL_LIST = `${Env.REACT_APP_API_URL}Referral/api/Referral/GetReferralsForChat`
const CREATE_THREAD = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/CreateReferralDiscussion`
const GET_REFERRAL_SOURCE_PDF_URL = `${Env.REACT_APP_API_URL}Referral/api/ReferralChatAttachment/GetAttachment`
const SEND_REFERRAL_CHAT_ATTACHMENT = `${Env.REACT_APP_API_URL}Referral/api/ReferralChatAttachment/UploadAttachment`
const DELETE_CHAT_ATTACHMENT_URL = `${Env.REACT_APP_API_URL}Referral/api/ReferralChatAttachment/DeleteAttachment`
const GET_CHAT_ACCESS_TOKEN = `${Env.REACT_APP_API_URL}Identity/api/DIscussionUser/GenerateSessionToken`
const GET_REFERRAL_HEADER = `${Env.REACT_APP_API_URL}Referral/api/Referral/GetReferralHeader`
const GET_PATIENT_DISCUSSIONS_STATUS = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/GetPatientDiscussionsStatus`
const CREATE_BULK_DISCUSSIONS = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/CreateBulkDiscussions`
const SEND_BROADCAST_MESSAGE = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/SendBroadcastMessage`
const GET_DISCUSSION_MEMBER_LIST = `${Env.REACT_APP_API_URL}Identity/api/DiscussionUser/GetDiscussionMemberList`
const ADD_USER_TO_DISCUSSION = `${Env.REACT_APP_API_URL}Identity/api/DiscussionUser/AddUserToDiscussion`
const IS_PARTICIPANT = `${Env.REACT_APP_API_URL}Identity/api/DiscussionUser/IsParticipant`
const REVALIDATE_DISCUSSION_PARTICIPANTS_URL = `${Env.REACT_APP_API_URL}Identity/api/DiscussionUser/ReValidateDiscussionParticipants`
// Pin Favorite Threads (US-16983).
const PIN_DISCUSSION = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/PinDiscussion`
const UNPIN_DISCUSSION = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/UnpinDiscussion`
const GET_MENTIONED_DISCUSSIONS = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/GetMentionedDiscussions`
const GET_PINNED_DISCUSSIONS = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/GetPinnedDiscussions`
const MESSAGE_POSTED = `${Env.REACT_APP_API_URL}Referral/api/ReferralDiscussion/MessagePosted`

export const getRefLists = (requestBody: unknown): Promise<CardResponse> =>
  HttpClient[ApiRequestTypes.post](REFERRAL_LIST, requestBody)

/** Re-pinning an already pinned thread is a no-op. */
export const pinDiscussion = (requestBody: PinDiscussionReqBody): Promise<PinDiscussionResponse> =>
  HttpClient[ApiRequestTypes.post](PIN_DISCUSSION, requestBody)

/** Removes only the calling user's pin. */
export const unpinDiscussion = (threadId: string): Promise<UnpinDiscussionResponse> =>
  HttpClient[ApiRequestTypes.del](`${UNPIN_DISCUSSION}?threadId=${encodeURIComponent(threadId)}`)

/**
 * Threads where the caller was mentioned, most recent mention first (US 18590).
 *
 * Covers direct mentions and @everyone, limited to the role and communities in view. Paged
 * by mention time rather than page number — pass the last entry's `mentionedAt` back as
 * `before` for the next page.
 * @param data page size, cursor and the selected role and communities.
 */
export const getMentionedDiscussions = (
  data: MentionedDiscussionsRequest,
): Promise<MentionedDiscussionsResponse> =>
  HttpClient[ApiRequestTypes.post](GET_MENTIONED_DISCUSSIONS, data)

/** The calling user's pinned threads, newest pin first. */
export const getPinnedDiscussions = (): Promise<PinnedDiscussionsResponse> =>
  HttpClient[ApiRequestTypes.get](GET_PINNED_DISCUSSIONS)

export const createThread = (requestBody: CreateThreadData): Promise<ThreadCreateResponse> =>
  HttpClient[ApiRequestTypes.post](CREATE_THREAD, requestBody)

export const getReferralSourcePdf = (id: number): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.get](`${GET_REFERRAL_SOURCE_PDF_URL}?fileId=${id}`)

export const sendReferralChatAttachment = (requestBody: unknown): Promise<CardResponse> =>
  HttpClient[ApiRequestTypes.post](SEND_REFERRAL_CHAT_ATTACHMENT, requestBody)

export const deleteChatAttachment = (id: number): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.get](`${DELETE_CHAT_ATTACHMENT_URL}?id=${id}`)

export const getChatSessionToken = (): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.get](GET_CHAT_ACCESS_TOKEN)

export const getReferralHeader = (
  patientFacilityId: number,
): Promise<{ data: DiscussionHeaderDataType }> =>
  HttpClient[ApiRequestTypes.get](`${GET_REFERRAL_HEADER}?patientFacilityId=${patientFacilityId}`)

export const getPatientDiscussionStatus = (
  patientId: number,
): Promise<PatiendDiscussionsStatusResponse> =>
  HttpClient[ApiRequestTypes.get](`${GET_PATIENT_DISCUSSIONS_STATUS}?patientId=${patientId}`)

export const createBulkDiscussions = (
  requestBody: CreateBulkDiscussionsRequestData,
): Promise<CreateBulkDiscussionsResponse> =>
  HttpClient[ApiRequestTypes.post](CREATE_BULK_DISCUSSIONS, requestBody)

export const sendBroadcastMessage = (
  requestBody: BroadcastMessageObject,
): Promise<BroadcastMessageResponse> =>
  HttpClient[ApiRequestTypes.post](SEND_BROADCAST_MESSAGE, requestBody)

export const isParticipant = (requestBody: IsParticipantReqBody): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](IS_PARTICIPANT, requestBody)

export const addUserToDiscussion = (requestBody: UserToDiscussionReqBody): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](ADD_USER_TO_DISCUSSION, requestBody)

export const getDiscussionMemberList = (
  requestBody: DiscussionMemberListReqBody,
): Promise<DiscussionMemberListResBody> =>
  HttpClient[ApiRequestTypes.post](GET_DISCUSSION_MEMBER_LIST, requestBody)

export const reValidateDiscussionParticipants = (
  requestBody: RevalidationReqBody,
): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](REVALIDATE_DISCUSSION_PARTICIPANTS_URL, requestBody)

/**
 * Tells the backend a discussion message was sent, so the thread's last-message time moves
 * and the other participants get a bell notification (18303).
 *
 * SendBird has no server-side hook for this, so the client is the only thing that knows a
 * message went out. The backend confirms the message with SendBird before writing anything
 * and ignores repeats, so calling it twice is harmless.
 * @param body the community the thread belongs to and the SendBird message id.
 */
export const discussionMessagePosted = (body: DiscussionMessagePostedRequest): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](MESSAGE_POSTED, body)

/**
 * Fire-and-forget wrapper used by every send path.
 *
 * A failure here must not surface: the message itself was delivered by SendBird, and the
 * only casualty is the recipients' bell entry.
 * @param channel the SendBird channel the message was sent to.
 * @param messageId the id SendBird returned for the sent message.
 */
export const notifyDiscussionMessagePosted = (
  channel: { cachedMetaData?: unknown } | null | undefined,
  messageId?: number,
): void => {
  const metaData = channel?.cachedMetaData as MetaDataType | undefined
  const patientFacilityId = Number(metaData?.patient_facility_id ?? 0)
  if (!patientFacilityId || !messageId) return

  discussionMessagePosted({ patientFacilityId, messageId }).catch(() => undefined)
}
